/**
 * src/components/teacher-dashboard-components/theme/components/Progress.js
 *
 * React Component Module.
 *
 * Purpose: React UI module containing one or more components and local UI helpers.
 *
 * Data flow (high level):
 * - UI/Caller -> this module -> downstream helpers/services -> state/response.
 */
import React from 'react';
import styled from 'styled-components';
import { useTheme } from '../ThemeContext';
import { getColor, createTransition } from '../themeUtils';

const ProgressContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
  width: 100%;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: ${props => props.$size === 'small' ? '0.25rem' : 
                     props.$size === 'large' ? '0.75rem' : '0.5rem'};
  background: ${getColor('neutral.medium')};
  border-radius: ${props => props.theme.borderRadius.full};
  overflow: hidden;
  position: relative;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: ${props => {
    switch (props.$variant) {
      case 'success': return getColor('semantic.success');
      case 'warning': return getColor('semantic.warning');
      case 'error': return getColor('semantic.error');
      case 'gradient': return props.theme.colors.gradients.primary;
      default: return getColor('primary.blue');
    }
  }};
  border-radius: ${props => props.theme.borderRadius.full};
  width: ${props => Math.min(Math.max(props.$value || 0, 0), 100)}%;
  ${createTransition('width', 'normal')};
  position: relative;
  
  ${props => props.$animated && `
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;
      background-image: linear-gradient(
        -45deg,
        rgba(255, 255, 255, 0.2) 25%,
        transparent 25%,
        transparent 50%,
        rgba(255, 255, 255, 0.2) 50%,
        rgba(255, 255, 255, 0.2) 75%,
        transparent 75%,
        transparent
      );
      background-size: 1rem 1rem;
      animation: move 1s linear infinite;
    }
    
    @keyframes move {
      0% { background-position: 0 0; }
      100% { background-position: 1rem 1rem; }
    }
  `}
`;

const ProgressLabel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: ${props => props.theme.typography.fontFamily.primary};
  font-size: ${props => props.theme.isMobile ? 
    props.theme.typography.mobile.small.fontSize : 
    props.theme.typography.desktop.small.fontSize};
  color: ${getColor('neutral.dark')};
`;

const ProgressText = styled.span`
  font-weight: 500;
  color: ${getColor('primary.blue')};
`;

const ProgressValue = styled.span`
  font-weight: 600;
  color: ${props => {
    switch (props.$variant) {
      case 'success': return getColor('semantic.success');
      case 'warning': return getColor('semantic.warning');
      case 'error': return getColor('semantic.error');
      default: return getColor('primary.blue');
    }
  }};
`;

/**
 * Progress.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */
const Progress = ({
  value = 0,
  max = 100,
  variant = 'primary',
  size = 'medium',
  animated = false,
  showLabel = false,
  label,
  showValue = false,
  className,
  ...props
}) => {
  const theme = useTheme();
  const percentage = Math.round((value / max) * 100);
  
  return (
    <ProgressContainer theme={theme} className={className} {...props}>
      {(showLabel || label || showValue) && (
        <ProgressLabel theme={theme}>
          {(label || showLabel) && (
            <ProgressText theme={theme}>
              {label || 'Progress'}
            </ProgressText>
          )}
          {showValue && (
            <ProgressValue theme={theme} $variant={variant}>
              {percentage}%
            </ProgressValue>
          )}
        </ProgressLabel>
      )}
      
      <ProgressBar theme={theme} $size={size}>
        <ProgressFill
          theme={theme}
          $value={percentage}
          $variant={variant}
          $animated={animated}
        />
      </ProgressBar>
    </ProgressContainer>
  );
};

export default Progress;
